<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();

const dialogRef = ref(null);
const reason = ref('');
const canConfirm = computed(() => reason.value.trim().length > 0);

watch(
  () => props.show,
  async visible => {
    if (!visible) return;
    reason.value = '';
    await nextTick();
    dialogRef.value?.open();
  }
);

const close = () => {
  dialogRef.value?.close();
  emit('close');
};

const confirm = () => {
  if (!canConfirm.value) return;
  emit('confirm', reason.value.trim());
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('TELETEL_REMINDERS.COMPLETE_DIALOG.TITLE')"
    :description="t('TELETEL_REMINDERS.COMPLETE_DIALOG.DESCRIPTION')"
    :cancel-button-label="t('TELETEL_REMINDERS.DIALOG.CANCEL')"
    :confirm-button-label="t('TELETEL_REMINDERS.COMPLETE_DIALOG.CONFIRM')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    @close="emit('close')"
    @confirm="confirm"
  >
    <div class="flex flex-col gap-2">
      <label
        for="reminder-completion-reason"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ t('TELETEL_REMINDERS.COMPLETE_DIALOG.REASON_LABEL') }}
      </label>
      <textarea
        id="reminder-completion-reason"
        v-model="reason"
        rows="4"
        maxlength="500"
        class="w-full !mb-0 rounded-lg border border-n-strong bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
        :placeholder="t('TELETEL_REMINDERS.COMPLETE_DIALOG.REASON_PLACEHOLDER')"
        :disabled="isLoading"
        @keydown.esc.prevent="close"
      />
      <p class="mb-0 text-xs text-n-slate-10">
        {{ t('TELETEL_REMINDERS.COMPLETE_DIALOG.REQUIRED') }}
      </p>
    </div>
  </Dialog>
</template>
